import { t } from '@lingui/macro'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { ActivityCard } from '@/modules/shared/components/activity-card/ActivityCard'
import {
  mapAppointmentsToTimeline,
  mapEncountersToTimeline,
} from '@/modules/timeline/components/timeline/helpers/mapping'
import { useRelatedAppointments, useRelatedEncounters } from '../../hooks/useReleated'
import { AnimatedLoader } from '../loader/Loader'
import { RelatedItemsList, RelatedItemsWrapper } from './Related.styled'

/**
 * Related triggers component (appointments and encounters)
 * @param activity - The activity to get related triggers for
 * @returns The related triggers component
 */
export const RelatedTriggers = ({ trigger }: { trigger: string }) => {
  const navigate = useNavigate()
  const { appointmentId, encounterTrigger } = useMemo(() => {
    if (!trigger) {
      return {}
    }

    const [type, id] = trigger.split('_')
    if (type === 'afspraak') {
      return { appointmentId: Number(id) }
    } else if (type === 'operatie') {
      return { encounterTrigger: trigger }
    }
    return {}
  }, [trigger])

  const { appointment, isLoading: appointmentIsLoading } = useRelatedAppointments(appointmentId)
  const { encounters, isLoading: encountersIsLoading } = useRelatedEncounters(encounterTrigger)

  if (appointmentIsLoading || encountersIsLoading) return <AnimatedLoader small />

  const appointmentTimeline = appointment
    ? mapAppointmentsToTimeline([appointment], navigate, true)?.map((activity) => (
        <ActivityCard {...activity} key={activity.id} showStart={false} />
      ))
    : []

  const encounterTimeline = encounters
    ? mapEncountersToTimeline(encounters, navigate, true)?.map((activity) => {
        return <ActivityCard {...activity} key={activity.id} showStart={false} />
      })
    : []

  if (appointmentTimeline.length === 0 && encounterTimeline.length === 0) {
    return null
  }

  return (
    <RelatedItemsWrapper>
      <H3>{t({ id: 'related.title', message: 'Gerelateerd' })}</H3>
      <RelatedItemsList>
        {appointmentTimeline}
        {encounterTimeline}
      </RelatedItemsList>
    </RelatedItemsWrapper>
  )
}
